<template>
    <UForm
        :schema="schema"
        :state="state"
        @submit="onSubmit"
        class="max-w-md mx-auto p-6 space-y-6 bg-white dark:bg-gray-800 rounded-lg shadow-md"
    >
        <h2 class="text-2xl font-semibold mb-6 text-center">{{ $t('Create your account') }}</h2>

        <UAlert v-if="error" close @update:open="error = false" variant="soft" color="error" :title="$t('Registration failed')" :description="$t('Please check your information and try again.')" class="mb-4" />

        <div class="space-y-4">
            <UFormField :label="$t('Email')" name="email">
                <UInput  class="w-full" size="lg" v-model="state.email" type="email" :placeholder="$t('Enter your email')" />
            </UFormField>

            <UFormField :label="$t('Password')" name="password">
                <UInput class="w-full" size="lg" v-model="state.password" type="password" :placeholder="$t('Enter your password')" />
            </UFormField>

            <UFormField :label="$t('Confirm Password')" name="passwordConfirm">
                <UInput class="w-full" size="lg" v-model="state.passwordConfirm" type="password" :placeholder="$t('Confirm your password')" />
            </UFormField>

            <UFormField name="agreeTerms" >
                <UCheckbox v-model="state.agreeTerms">
                   <template #label>
                          {{ $t('I agree to the') }} <ULink class="text-primary underline" @click.prevent="openTerms = true" >{{ $t('terms and conditions') }}</ULink> and <ULink class="text-primary underline" @click.prevent="openPrivacyPolicy = true" >{{ $t('privacy policy') }}</ULink>
                   </template>
                </UCheckbox>
            </UFormField>
            <UFormField name="agreeMarketing" >
                <UCheckbox v-model="state.agreeMarketing">
                   <template #label>
                          {{ $t('I agree to receiving personalized marketing communications') }} 
                   </template>
                </UCheckbox>
            </UFormField>
            <UFormField name="agreeLoyality" >
                <UCheckbox v-model="state.agreeLoyality">
                   <template #label>
                          {{ $t('I agree to the') }} <ULink class="text-primary underline" @click.prevent="openLoyality = true" >{{ $t('loyalty program') }}</ULink>
                   </template>
                </UCheckbox>
            </UFormField>
        </div>

        <div class="flex justify-between">
            <UButton :loading="customerStore.pending" :disabled="customerStore.pending" loading-icon="i-lucide-loader" type="submit" color="primary" variant="solid" :label="$t('Register')" size="lg" />
        </div>
        <USeparator :label="$t('or')" class="my-4" />
        <div class="flex justify-center">
            <UButton type="button" color="secondary" variant="outline" :label="$t('Register with Google')" size="lg" />
        </div>
        <USeparator :label="$t('Already have an account?')" class="my-4" />
        <div class="text-center">
            <UButton :href="$localePath({ name: 'login' })" variant="outline" color="primary">{{ $t('Login') }}</UButton>
        </div>
    </UForm>

    <TermsModal v-model:open="openTerms" />
    <PrivacyPolicyModal v-model:open="openPrivacyPolicy" />
    <UModal v-model:open="openLoyality" :title="$t('Loyalty Program')" size="xl">
        <template #body>
                <div class="p-4 max-h-96 overflow-y-auto">
            <p>
                {{ $t('Here is the loyalty program information...') }}
            </p>
            <!-- Add actual loyalty program content here -->
        </div>
        </template>
        <template #footer>
            <UButton @click="openLoyality = false" color="primary">{{ $t('Close') }}</UButton>
        </template>
    </UModal>
</template>
<script setup lang="ts">
import type { FormSubmitEvent } from '@nuxt/ui'
import { z } from 'zod'

const {
    email
} = defineProps<{
    email?: string
}>()

const emit = defineEmits<{
    (e: 'register-success'): void
}>()

const { t } = useI18n()
const config = useRuntimeConfig()

const schema = z.object({
    email: z.string().email(t('Invalid email address')),
    password: z.string().min(config.public.passwordMinLength, t('Password must be at least {n} characters', { n: config.public.passwordMinLength })),
    passwordConfirm: z.string().min(1, t('Please confirm your password')),
    agreeTerms: z.boolean().refine((val) => val === true, {
        message: t('You must agree to the terms and conditions')
    }),
    agreeMarketing: z.boolean(),
    agreeLoyality: z.boolean()
}).refine((data) => data.password === data.passwordConfirm, {
    message: t('Passwords do not match'),
    path: ['passwordConfirm']

})

type SchemaOut = z.output<typeof schema>

const state = reactive<SchemaOut>({
    email: email || '',
    password: '',
    passwordConfirm: '',
    agreeTerms: false,
    agreeMarketing: false,
    agreeLoyality: false
})

const customerStore = useCustomerStore()
const toast = useToast()
const error = ref<boolean>(false)

watch(state, () => {
    error.value = false
})

const onSubmit = async ({ data } : FormSubmitEvent<SchemaOut>) => {
    try {
        await customerStore.register(data);
        toast.add({
            title: 'Registration success',
            description: 'Your account has been created successfully.',
            color: 'success',
        });
        emit('register-success');
    } catch (e) {
        console.error('Registration failed:', e);
        error.value = true
    }
}

const openTerms = ref(false);
const openPrivacyPolicy = ref(false);
const openLoyality = ref(false);

</script>